import { DistrictDto } from '@families-link/shared';
import { useAppDispatch, useAppSelector } from '../../../store';
import { debounce } from 'lodash';
import { useState, useRef, useCallback, useEffect } from 'react';
import { DefaultValues, useForm } from 'react-hook-form';
import { setDistricts } from '../../survey/store/survey-filters.slice';

export type DistrictFilterFormValues = {
	districts: DistrictDto[];
};

export const useDistrictSurveyLocalFilter = () => {
	const dispatch = useAppDispatch();
	const reduxDistricts = useAppSelector((state) => state.surveyFilters.districts);

	const defaultValues: DefaultValues<DistrictFilterFormValues> = {
		districts: [],
	};

	const { register, control, watch, setValue, getValues } = useForm({
		defaultValues,
	});

	const name = 'districts' as const;
	const currentDistricts = watch(name) as DistrictDto[];

	const [districts, setDistrictsState] = useState<DistrictDto[]>([]);

	const prevDistrictRef = useRef<string[]>([]);

	const debouncedSetDistrictsState = useCallback(
		debounce((list: DistrictDto[]) => {
			setDistrictsState(list);
		}, 300),
		[]
	);

	const debouncedSetDistrictsRedux = useCallback(
		debounce((list: DistrictDto[]) => {
			dispatch(setDistricts(list));
		}, 300),
		[dispatch]
	);

	useEffect(() => {
		const currentIds = currentDistricts.map((d) => d._id).sort();
		const prevIds = prevDistrictRef.current;

		const hasChanged = JSON.stringify(currentIds) !== JSON.stringify(prevIds);

		if (hasChanged) {
			prevDistrictRef.current = currentIds;
			debouncedSetDistrictsState(currentDistricts);
			debouncedSetDistrictsRedux(currentDistricts);
		}
	}, [currentDistricts, debouncedSetDistrictsState, debouncedSetDistrictsRedux]);

	useEffect(() => {
		const currentFormIds = (getValues(name) as DistrictDto[]).map((d) => d._id).sort();
		const reduxIds = reduxDistricts.map((d) => d._id).sort();

		const isSame = JSON.stringify(currentFormIds) === JSON.stringify(reduxIds);

		if (!isSame) {
			setValue(name, reduxDistricts);
			setDistrictsState(reduxDistricts);
		}
	}, [reduxDistricts, getValues, name, setValue]);

	return {
		register,
		getValues,
		control,
		districts,
		districtIds: districts.map((d) => d._id),
	};
};
